import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Card, Label, Textarea, TextInput } from 'flowbite-react';
import { trim } from 'lodash-es';
import { lazy, Suspense, useRef, useState } from 'react';
import { HiInformationCircle } from 'react-icons/hi';
import { useOutletContext } from 'react-router-dom';

import { postStoreInfo, updateStoreInfo } from 'src/api/methods';
import { mutations } from 'src/api/mutations';
import type { DashboardContext } from 'src/routes/dashboard/container';
import { assertDefined, fileExtension } from 'src/utils';

const FileUploadDropzone = lazy(() =>
  import('src/components/file-upload-dropzone').then((module) => ({
    default: module.FileUploadDropzone,
  })),
);

export function DashboardStore() {
  const { user, store, userProfile } = useOutletContext<DashboardContext>();
  const [contactAddressesInputValue, setContactAddressesInputValue] = useState(
    store ? store.contact_addresses.join(', ') : '',
  );
  const [storeCompanyNameInputValue, setStoreCompanyNameInputValue] = useState(
    store ? store.company_name : '',
  );
  const [updateLogoValue, setUpdateLogoValue] = useState(false);
  const [saveStoreIsPending, setSaveStoreIsPending] = useState(false);
  const uploadUrlMutationForUser = useMutation(mutations.signedLogoUploadUrlForUser());
  const didUploadLogoMutationForUser = useMutation(mutations.didUploadLogoForUser());

  const [storeCompanyNameError, setStoreCompanyNameError] = useState('');
  const [storeContactAddressesError, setStoreContactAddressesError] = useState('');
  const [storeLogoError, setStoreLogoError] = useState('');
  const [pendingUploads, setPendingUploads] = useState<
    { filename: string; uploadFilename: string }[]
  >([]);
  const uploadFilenames = useRef({ logoFilename: '' });

  const userLogoMediaFilename = (inputName: string) => {
    return `logo.${fileExtension(inputName)}`;
  };

  const saveStoreInfo = () => {
    console.log('saveStoreInfo');
    setStoreCompanyNameError('');
    setStoreContactAddressesError('');
    setStoreLogoError('');

    const contactAddresses = contactAddressesInputValue.split(',').map(trim); // convert string to array

    assertDefined(storeCompanyNameInputValue);
    assertDefined(contactAddresses);

    if (store) {
      updateStoreInfo(store.key_name, storeCompanyNameInputValue, contactAddresses, updateLogoValue)
        .then((resData) => {
          console.log(resData);
          window.location.assign('/app/' + store.key_name + '/store/');
        })
        .catch((res) => {
          if (typeof res === 'string' || res instanceof String) {
            setStoreCompanyNameError('Refresh this page to load your store info.');
          } else {
            if ('company_name' in res) {
              setStoreCompanyNameError(res.company_name[0]);
            }
            if ('contact_addresses' in res) {
              setStoreContactAddressesError(res.contact_addresses[0]);
            }
          }
        });
    } else {
      // todo: backend should control setting of the keyName
      const keyName = storeCompanyNameInputValue
        .replace('&', 'and')
        .replace('+', 'and')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .toLowerCase();
      if (!updateLogoValue && (!userProfile || userProfile.state !== 'NEW_USER')) {
        setStoreLogoError('Upload a store logo before saving');
      } else {
        postStoreInfo(keyName, storeCompanyNameInputValue, contactAddresses, updateLogoValue)
          .then((resData) => {
            if (resData?.data) {
              window.location.assign('/app/' + resData.data.key_name + '/video-flows/');
            }
          })
          .catch((res) => {
            if (typeof res === 'string' || res instanceof String) {
              setStoreCompanyNameError('Refresh this page to load your store info.');
            } else {
              if ('key_name' in res) {
                setStoreCompanyNameError(res.key_name[0]);
              }
              if ('company_name' in res) {
                setStoreCompanyNameError(res.company_name[0]);
              }
              if ('contact_addresses' in res) {
                setStoreContactAddressesError(res.contact_addresses[0]);
              }
            }
          });
      }
    }
  };

  return (
    <Card>
      <div id='create-store-content'>
        {!store?.company_name && (
          <div className='mb-12 flex w-full'>
            <p className='text-md font-medium text-gray-900'>
              Enter your store&apos;s basic information in the fields below
            </p>
          </div>
        )}
        <div className='mb-6 grid gap-12 md:grid-cols-2'>
          <div className='w-full'>
            <div className='mb-6'>
              {!store?.company_name && (
                <>
                  <Label
                    color={storeCompanyNameError ? 'failure' : 'gray'}
                    htmlFor='companyName'
                    className='mb-2 block text-sm font-medium'
                  >
                    Company name <span className='text-base text-rose-600'>*</span>
                  </Label>
                  <TextInput
                    color={storeCompanyNameError ? 'failure' : 'gray'}
                    sizing='sm'
                    id='companyName'
                    helperText={
                      storeCompanyNameError ? (
                        <>
                          <span className='font-medium'>Oops!</span> {storeCompanyNameError}
                        </>
                      ) : (
                        ''
                      )
                    }
                    placeholder='Maverick'
                    value={storeCompanyNameInputValue}
                    onChange={(e) => setStoreCompanyNameInputValue(e.target.value)}
                    required
                  ></TextInput>
                </>
              )}
              {store?.company_name && (
                <p className='text-3xl font-medium text-gray-900'>{store.company_name}</p>
              )}
            </div>
            <div>
              <Label
                color={storeContactAddressesError ? 'failure' : 'gray'}
                htmlFor='contactAddresses'
                className='mb-2 block text-sm font-medium text-gray-900'
              >
                Contact addresses <span className='text-base text-rose-600'>*</span>
              </Label>
              <Textarea
                id='contactAddresses'
                color={storeContactAddressesError ? 'failure' : 'gray'}
                rows={4}
                className='block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500'
                placeholder='jane@example.com'
                helperText={
                  storeContactAddressesError ? (
                    <>
                      <span className='font-medium'>Oops!</span> {storeContactAddressesError}
                    </>
                  ) : (
                    ''
                  )
                }
                value={contactAddressesInputValue}
                onChange={(e) => setContactAddressesInputValue(e.target.value)}
              ></Textarea>
              <p id='helper-text-explanation' className='mt-2 text-sm text-gray-500'>
                List the emails of anyone who should receive analytics and product updates with a
                comma between each email address.
              </p>
            </div>
          </div>
          <div className='mb-6 flex w-full items-center justify-center'>
            <div className='w-full'>
              <Label value={store?.logo ? 'Replace store logo' : 'Upload store logo'} />
              {storeLogoError && (
                <Alert color='failure' icon={HiInformationCircle}>
                  <span>
                    <p>
                      <span className='font-medium'>A logo is required!</span> {storeLogoError}
                    </p>
                  </span>
                </Alert>
              )}
              <Suspense>
                <FileUploadDropzone
                  subtitle='Upload a JPG or PNG file'
                  showPreview={true}
                  allowedExtensions={['jpg', 'jpeg', 'png']}
                  accept='image/jpg,image/jpeg,image/png'
                  method='PUT'
                  allowReplacingExistingFile={true}
                  existingFile={store ? store.logo : undefined}
                  hasExistingFile={store ? !!store.logo : false}
                  uploadUrl={async (inputName) => {
                    setStoreLogoError('');
                    const { signed_url, upload_filename } =
                      await uploadUrlMutationForUser.mutateAsync([
                        user.id,
                        userLogoMediaFilename(inputName),
                      ]);
                    uploadFilenames.current.logoFilename = upload_filename;
                    return signed_url;
                  }}
                  onStart={async () => {
                    return true;
                  }}
                  onComplete={async (inputName) => {
                    setPendingUploads((current) => [
                      ...current,
                      {
                        filename: userLogoMediaFilename(inputName),
                        uploadFilename: uploadFilenames.current.logoFilename,
                      },
                    ]);
                    setUpdateLogoValue(true);
                    return true;
                  }}
                />
              </Suspense>
            </div>
          </div>
        </div>
        <div className='flow-root'>
          <Button
            className='float-right mb-2 mr-2 rounded-lg bg-gradient-to-br from-purple-600 to-blue-500 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-gradient-to-bl focus:outline-none focus:ring-4 focus:ring-blue-300'
            disabled={
              !store &&
              (storeCompanyNameInputValue === '' ||
                contactAddressesInputValue === '' ||
                ((!userProfile || userProfile.state !== 'NEW_USER') && pendingUploads.length === 0))
            }
            onClick={async () => {
              const toWait: Promise<never>[] = [];
              const uploads = [...pendingUploads];
              setPendingUploads([]);
              setSaveStoreIsPending(true);
              if (pendingUploads.length > 0) {
                for (const toUpload of uploads) {
                  const promise = didUploadLogoMutationForUser.mutateAsync([
                    user.id,
                    toUpload.filename,
                    toUpload.uploadFilename,
                  ]);
                  toWait.push(promise);
                }
                await Promise.all(toWait).then((value) => {
                  console.log(value);
                  saveStoreInfo();
                });
              } else {
                saveStoreInfo();
              }
            }}
          >
            {(didUploadLogoMutationForUser.isPending || saveStoreIsPending) && (
              <FontAwesomeIcon icon={solid('spinner')} spin className='mr-3' />
            )}
            Save
          </Button>
        </div>
      </div>
    </Card>
  );
}
